import DomainsPaperBody from '@components/EntityDrawers/components/inputs/DomainDropdown/components/DomainsPaperBody';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import Label from '@components/Inputs/Label';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Popper,
  TextField,
  autocompleteClasses,
  useAutocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useSlot from '@mui/material/utils/useSlot';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { themePalette } from '@theme/muiTheme';
import { findWideDomain } from '@utils/domains';
import { CloseCircleIcon, DropdownIcon } from '@utils/iconsDefs';
import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

type Props = {
  options: ISelectOption[];
  readOnly: boolean;
  label: string;
  required: boolean;
};

const AutocompletePopper = styled(Popper, {
  name: 'MuiAutocomplete',
  slot: 'Popper',
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [
      { [`& .${autocompleteClasses.option}`]: styles.option },
      styles.popper,
      ownerState.disablePortal && styles.popperDisablePortal,
    ];
  },
})(({ theme }) => ({
  zIndex: theme.zIndex.modal,
  variants: [
    {
      props: { disablePortal: true },
      style: {
        position: 'absolute',
      },
    },
  ],
}));

const AutocompletePaper = styled(Paper, {
  name: 'MuiAutocomplete',
  slot: 'Paper',
})(() => ({ overflow: 'auto' }));

const AutocompleteEndAdornment = styled('div', {
  name: 'MuiAutocomplete',
  slot: 'EndAdornment',
})({
  // We use a position absolute to support wrapping tags.
  position: 'absolute',
  right: 9,
  top: '50% !important',
  transform: 'translate(0, -50%)',
});

const AutocompleteClearIndicator = styled(IconButton, {
  name: 'MuiAutocomplete',
  slot: 'ClearIndicator',
})({
  marginRight: -2,
  padding: 4,
  visibility: 'hidden',
});

const AutocompletePopupIndicator = styled(IconButton, {
  name: 'MuiAutocomplete',
  slot: 'PopupIndicator',
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [
      styles.popupIndicator,
      ownerState.popupOpen && styles.popupIndicatorOpen,
    ];
  },
})({
  padding: 2,
  marginRight: -2,
  variants: [
    {
      props: { popupOpen: true },
      style: {
        transform: 'rotate(180deg)',
      },
    },
  ],
});

const AutocompleteRoot = styled('div', {
  name: 'MuiAutocomplete',
  slot: 'Root',
})(() => ({
  position: 'relative',
  [`&:hover .clearIndicator`]: {
    visibility: 'visible !important',
  },
}));

const DomainsInput: FC<Props> = ({ options, readOnly, required, label }) => {
  const projectId = useProjectId();

  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const wideDomain = findWideDomain(projectDomains ?? []);

  const { setValue } = useFormContext();

  const selectedDomains: ISelectOption[] = useWatch({
    name: CommonKeys.Domains,
  });

  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getClearProps,
    getPopupIndicatorProps,
    dirty,
    expanded,
    popupOpen,
    anchorEl,
    setAnchorEl,
    value,
    focused,
  } = useAutocomplete({
    options,
    value: selectedDomains ?? [],
    disableCloseOnSelect: true,
    multiple: true,
    getOptionLabel: (option) => option.label,
    isOptionEqualToValue: (option, value) => option.value === value.value,
    onChange: (_, value) => {
      setValue(CommonKeys.Domains, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    disabled: readOnly,
    readOnly,
  });

  const isWideSelected = value.some(({ value }) => value === wideDomain?.id);

  const hasClearIcon = dirty && !readOnly;
  const hasPopupIcon = true;
  const { onMouseDown: handleInputMouseDown } = getInputProps();

  const ownerState = {
    classes: '',
    disablePortal: false,
    expanded,
    focused,
    fullWidth: true,
    hasClearIcon,
    hasPopupIcon,
    inputFocused: focused,
    popupOpen,
    size: 'sm',
  };

  const [PaperSlot, paperProps] = useSlot('paper', {
    elementType: Paper,
    externalForwardedProps: {},
    ownerState,
    className: 'paper',
  });

  const [PopperSlot, popperProps] = useSlot('popper', {
    elementType: Popper,
    externalForwardedProps: {},
    ownerState,
    className: 'popper',
    additionalProps: {
      disablePortal: true,
      style: { width: anchorEl ? anchorEl.clientWidth : 0 },
      role: 'presentation',
      anchorEl,
      open: popupOpen,
    },
  });

  return (
    <Box>
      <Label required={required}>{label}</Label>

      <AutocompleteRoot {...getRootProps()} className="root">
        <TextField
          fullWidth
          disabled={readOnly}
          placeholder={value.length ? '' : 'Select target scope'}
          InputProps={{
            ref: setAnchorEl,
            sx: { gap: 1, flexWrap: 'wrap', paddingRight: '65px !important' },
            startAdornment: value
              .filter(({ value: domainValue }) =>
                isWideSelected ? domainValue === wideDomain?.id : true,
              )
              .map(({ value, label }, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={value}
                  label={label}
                  size="small"
                />
              )),
            onMouseDown: (event) => {
              if (event.target === event.currentTarget && !readOnly) {
                handleInputMouseDown?.(event as any);
              }
            },
            ...((hasClearIcon || hasPopupIcon) && {
              endAdornment: (
                <AutocompleteEndAdornment className="endAdornment">
                  {hasClearIcon ? (
                    <AutocompleteClearIndicator
                      className="clearIndicator"
                      {...(getClearProps() as any)}
                    >
                      <CloseCircleIcon
                        fill={themePalette.system.error}
                        width={20}
                        height={20}
                      />
                    </AutocompleteClearIndicator>
                  ) : null}

                  {hasPopupIcon ? (
                    <AutocompletePopupIndicator
                      ownerState={ownerState as any}
                      {...(getPopupIndicatorProps() as any)}
                      className="popupIndicator"
                      disabled={readOnly}
                    >
                      <DropdownIcon
                        className={`dropdown ${
                          value?.length > 0 ? 'hasValue' : ''
                        }`}
                      />
                    </AutocompletePopupIndicator>
                  ) : null}
                </AutocompleteEndAdornment>
              ),
            }),
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{
            ...getInputProps(),
            disabled: readOnly,
            readOnly,
            sx: { flex: 1 },
          }}
        />

        {anchorEl && (
          <AutocompletePopper as={PopperSlot} {...(popperProps as any)}>
            <AutocompletePaper as={PaperSlot} {...paperProps} sx={{ py: 1 }}>
              <DomainsPaperBody />
            </AutocompletePaper>
          </AutocompletePopper>
        )}
      </AutocompleteRoot>
    </Box>
  );
};

export default DomainsInput;
